import _ from 'lodash'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useTableColumnVisibility, useTablePagination, useTableSorting } from '../../redux/table/hooks'
import {
  ProfileManagementActions,
  areInvitedUsersLoading,
  getInvitedUsersPageSize,
  getProfileManagementFilters,
  isFilteringActive
} from '../../redux/account/profile-management'
import { ColumnType, FiltersMetaRowWrapper, Table } from '../../components/Table'
import { RootState } from '../../redux/types'
import { areDomainsLoading, DomainType } from '../../redux/domains'

import { InvitedUser } from '../../redux/account/profile-management'

import { ProfileManagementEmptyView } from './ProfileManagementEmptyView'
import { dateFilterKeys } from './dateFilterKeys'
import { filterKeysToTranslationsMapper } from './filterKeysToTranslationsMapper'

const domainTypes = [DomainType.Role]

export type ProfileManagementTableProps = {
  tableColumns: ColumnType<InvitedUser>[]
  tableData: InvitedUser[] | undefined
}

export const ProfileManagementTable = ({ tableColumns, tableData }: ProfileManagementTableProps) => {
  const dispatch = useDispatch()
  const invitedUsersLoading = useSelector(areInvitedUsersLoading)
  const domainsLoading = useSelector((state: RootState) => areDomainsLoading(state, { domains: domainTypes }))
  const filters = useSelector(getProfileManagementFilters)
  const pageSize = useSelector(getInvitedUsersPageSize)
  const isFiltering = useSelector(isFilteringActive)

  useEffect(() => {
    dispatch(
      ProfileManagementActions.loadInvitedUsers({
        page: 1,
        pageSize
      })
    )
  }, [dispatch, pageSize])

  const tableOptions = {
    ...useTableSorting('profileManagement'),
    ...useTablePagination('profileManagement'),
    ...useTableColumnVisibility('profileManagement')
  }

  const filtersMetaRow = useCallback(
    () => (
      <FiltersMetaRowWrapper
        filters={filters}
        dateFilterKeys={dateFilterKeys}
        filterKeysToTranslationsMapper={filterKeysToTranslationsMapper}
        onClose={filterKey => dispatch(ProfileManagementActions.clearFilter({ filterKey }))}
        translationGroups={['form', 'dialogs']}
      />
    ),
    [filters, dispatch]
  )
  const renderMetaRow = _.keys(filters).length > 0 ? filtersMetaRow : undefined

  return (
    <Table<InvitedUser>
      rowKey="id"
      dataSource={tableData}
      columns={tableColumns}
      renderMetaRow={renderMetaRow}
      loading={invitedUsersLoading || domainsLoading}
      renderEmpty={
        isFiltering || invitedUsersLoading || domainsLoading ? undefined : () => <ProfileManagementEmptyView />
      }
      {...tableOptions}
    />
  )
}
